import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { SectionHeader } from "@/components/common/Widgets";
import { toast } from "sonner";
import { Plus, Users, ShieldCheck } from "lucide-react";

const ROLES = [
  { code: "FO", label: "Field Officer", requires_branch: true },
  { code: "BM", label: "Branch Manager", requires_branch: true },
  { code: "CO", label: "Credit Officer", requires_branch: true },
  { code: "RO", label: "Regional Office", requires_branch: false },
  { code: "HO", label: "Head Office Admin", requires_branch: false },
  { code: "SUPER_ADMIN", label: "Super Admin (multi-tenant)", requires_branch: false },
];

export default function AdminPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [regions, setRegions] = useState([]);
  const [form, setForm] = useState({
    first_name: "", middle_name: "", last_name: "",
    email: "", mobile: "", employee_id: "",
    password: "", role: "FO", branch: "", region: "",
    department: "", dob: "", address: "",
  });
  const [busy, setBusy] = useState(false);
  const canManage = user?.role === "HO" || user?.role === "RO" || user?.role === "SUPER_ADMIN";
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const load = async () => {
    if (!canManage) return;
    try {
      const r = await api.get("/auth/users");
      setUsers(r.data);
      const b = await api.get("/master/branches");
      setBranches(b.data);
      // Derive regions from branches (unique)
      const uniq = Array.from(new Set((b.data || []).map(x => x.region).filter(Boolean)));
      setRegions(uniq.length ? uniq : ["East", "West", "Central", "North", "South"]);
    } catch (e) {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [canManage]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const roleObj = ROLES.find(r => r.code === form.role) || ROLES[0];
  const requiresRegion = form.role === "RO";

  const validate = () => {
    if (!form.first_name) return "First name is required";
    if (!form.last_name) return "Last name is required";
    if (!form.email || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) return "Valid email is required";
    if (!form.mobile || form.mobile.length < 10) return "10-digit mobile is required";
    if (!form.employee_id) return "Employee ID is required";
    if (!form.password || form.password.length < 6) return "Password must be at least 6 characters";
    if (!form.role) return "Role is required";
    if (roleObj.requires_branch && !form.branch) return "Branch is required for this role";
    if (requiresRegion && !form.region) return "Region is required for Regional Office";
    return null;
  };

  const add = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setBusy(true);
    try {
      // Strip empty region for non-RO roles unless explicitly set
      const payload = { ...form };
      if (!payload.region) delete payload.region;
      await api.post("/auth/register", payload);
      toast.success("User created");
      setForm({ first_name: "", middle_name: "", last_name: "", email: "", mobile: "", employee_id: "", password: "", role: "FO", branch: "", region: "", department: "", dob: "", address: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || (e?.response?.data?.detail?.[0]?.msg) || "Failed"); } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/auth/users/${u.id}/activate?active=${!u.is_active}`);
      toast.success(`User ${!u.is_active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5">
      <SectionHeader title="User Administration" subtitle="Create and manage users. All fields marked * are mandatory." />
      {!canManage && <Card className="p-4 text-sm text-muted-foreground">You need SUPER_ADMIN, HO, or RO privileges to manage users.</Card>}
      {canManage && (
        <>
          <Card className="p-4 space-y-3">
            <div className="flex items-center gap-2"><Plus className="h-4 w-4 text-accent" /><h3 className="text-sm font-semibold heading">Create User</h3></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label={`${t("forms.firstName")} *`}><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="user-first-name" /></Field>
              <Field label={t("forms.middleName")}><Input value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} data-testid="user-middle-name" /></Field>
              <Field label={`${t("forms.lastName")} *`}><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="user-last-name" /></Field>
              <Field label={`${t("forms.email")} *`}><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="user-email" /></Field>
              <Field label={`${t("forms.mobile")} (10 digits) *`}><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} required data-testid="user-mobile" /></Field>
              <Field label={`${t("forms.employeeId")} *`}><Input value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} required data-testid="user-emp-id" /></Field>
              <Field label={`${t("forms.password")} * (min 6)`}><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid="user-password" /></Field>
              <Field label={`${t("common.role")} *`}>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.filter(r => isSuperAdmin || r.code !== "SUPER_ADMIN").map(r => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`${t("common.branch")} ${roleObj.requires_branch ? "*" : "(optional)"}`}>
                <Select value={form.branch} onValueChange={(v) => set("branch", v)} disabled={!roleObj.requires_branch && form.role !== "BM" && form.role !== "FO" && form.role !== "CO"}>
                  <SelectTrigger data-testid="user-branch"><SelectValue placeholder={roleObj.requires_branch ? "Select branch" : "Not applicable"} /></SelectTrigger>
                  <SelectContent>
                    {branches.map(b => <SelectItem key={b.id} value={b.name}>{b.name} ({b.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`${t("common.region")} ${requiresRegion ? "*" : "(auto)"}`}>
                <Select value={form.region} onValueChange={(v) => set("region", v)} disabled={form.role !== "RO" && !["HO","SUPER_ADMIN"].includes(form.role) ? false : false}>
                  <SelectTrigger data-testid="user-region"><SelectValue placeholder={requiresRegion ? "Select region" : "Auto from branch"} /></SelectTrigger>
                  <SelectContent>
                    {regions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("forms.department")}><Input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="e.g., Credit, Operations" /></Field>
              <Field label={t("forms.dob")}><Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} /></Field>
              <Field label={t("forms.address")} className="md:col-span-3"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
              <div className="md:col-span-3 flex justify-end"><Button onClick={add} disabled={busy} data-testid="admin-add-user-button"><Plus className="h-4 w-4 mr-1" />{busy ? "Creating…" : "Create User"}</Button></div>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b bg-muted/30 text-sm font-semibold heading flex items-center gap-2"><Users className="h-4 w-4" />Users {isSuperAdmin && "(across all banks)"}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr><th className="text-left px-3 py-2.5">Name</th><th className="text-left px-3 py-2.5">Email</th><th className="text-left px-3 py-2.5">Mobile</th><th className="text-left px-3 py-2.5">Role</th><th className="text-left px-3 py-2.5">Branch</th><th className="text-left px-3 py-2.5">Region</th><th className="text-left px-3 py-2.5">Emp ID</th><th className="text-left px-3 py-2.5">Status</th><th></th></tr>
                </thead>
                <tbody>
                  {users.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">No users.</td></tr>}
                  {users.map(u => (
                    <tr key={u.id} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{u.name}</td>
                      <td className="px-3 py-2 mono text-xs">{u.email}</td>
                      <td className="px-3 py-2 mono text-xs">{u.mobile || "—"}</td>
                      <td className="px-3 py-2"><Badge variant={u.role === "SUPER_ADMIN" ? "default" : "secondary"} className="text-xs">{u.role}</Badge></td>
                      <td className="px-3 py-2 text-xs">{u.branch || "—"}</td>
                      <td className="px-3 py-2 text-xs">{u.region ? <Badge variant="outline" className="text-accent border-accent/40 text-[10px]">{u.region}</Badge> : <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2 mono text-xs">{u.employee_id || "—"}</td>
                      <td className="px-3 py-2">{u.is_active === false ? <Badge variant="outline" className="text-destructive border-destructive/40">Disabled</Badge> : <Badge variant="outline" className="text-success border-success/40"><ShieldCheck className="h-3 w-3 mr-1 inline" />Active</Badge>}</td>
                      <td className="px-3 py-2 text-right">{(isSuperAdmin || user?.role === "HO") && u.email !== user?.email && (<Switch checked={u.is_active !== false} onCheckedChange={() => toggleActive(u)} data-testid={`admin-toggle-${u.id}`} />)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
function Field({ label, children, className = "" }) {
  return (<div className={`space-y-1.5 ${className}`}><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>);
}
